import { useEffect, useState } from "react";
import { api, API_URL, inr, fmtDate } from "@/lib/api";
import { BookOpen, Download, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { LedgerRangeBar } from "@/components/LedgerRangeBar";

const Stat = ({ label, value, icon: Icon, tone }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
    <span className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${tone}`}><Icon size={17} strokeWidth={1.6} /></span>
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-lg font-display font-bold text-slate-900 tabular-nums">{inr(value)}</p>
    </div>
  </div>
);

export default function PortalLedger() {
  const [d, setD] = useState(null);
  const [lr, setLr] = useState({ key: "all", from: "", to: "" });
  useEffect(() => {
    api.get(`/portal/ledger?date_from=${lr.from}&date_to=${lr.to}`).then((r) => setD(r.data)).catch(() => {});
  }, [lr.from, lr.to]);
  const entries = d?.entries || [];

  return (
    <div className="space-y-4" data-testid="portal-ledger">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-display font-bold text-slate-900 tracking-tight">Ledger</h1>
        <a href={`${API_URL}/portal/ledger/pdf?date_from=${lr.from}&date_to=${lr.to}`} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-md px-5 py-2.5 text-xs font-semibold transition-colors duration-200"
           data-testid="ledger-pdf">
          <Download size={13} /> Download Statement
        </a>
      </div>
      <LedgerRangeBar lr={lr} onApply={setLr} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Total Invoiced" value={d?.total_invoiced || 0} icon={TrendingUp} tone="text-sky-700 bg-sky-50 border-sky-100" />
        <Stat label="Total Paid" value={d?.total_paid || 0} icon={TrendingDown} tone="text-emerald-700 bg-emerald-50 border-emerald-100" />
        <Stat label="Balance Due" value={d?.balance || 0} icon={Wallet} tone="text-[#C94A4A] bg-[#FC5F5F]/10 border-[#FC5F5F]/25" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider text-left">
            <tr>
              <th className="px-4 py-3 w-[14%]">Date</th>
              <th className="px-4 py-3 w-[14%]">Type</th>
              <th className="px-4 py-3">Particulars</th>
              <th className="px-4 py-3 text-right w-[13%]">Debit</th>
              <th className="px-4 py-3 text-right w-[13%]">Credit</th>
              <th className="px-4 py-3 text-right w-[14%]">Balance</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={i} className="border-t border-slate-100 hover:bg-slate-50 transition-colors duration-150" data-testid={`ledger-row-${i}`}>
                <td className="px-4 py-3 whitespace-nowrap">{fmtDate(e.date)}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ${e.type === "Invoice" ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>{e.type}</span>
                </td>
                <td className="px-4 py-3 text-slate-700">{e.ref || "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums">{e.debit ? inr(e.debit) : "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{e.credit ? inr(e.credit) : "—"}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold">{inr(e.balance)}</td>
              </tr>
            ))}
            {d !== null && entries.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-sm text-slate-400" data-testid="ledger-empty">
                <BookOpen size={20} className="mx-auto mb-2 opacity-40" />No ledger entries yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
